/**
 * /cost — every usage event across every organization, the way the Tasks
 * page lists every task.
 *
 * Two tables. The summary is one row per organization with usage in the
 * period, each opening that organization's own Cost page. The ledger is one
 * row per event, newest first, its lines summarized: the costed sum and the
 * worst status, with the lines a click away on the task's Cost tab. The gap
 * banner covers the whole period, because a missing rate or a missing
 * exchange rate looks exactly like something cheap.
 */
import { listCapabilities } from '@optum/api-client/endpoints/capabilities';
import {
  exportCostEvents,
  getCostChoices,
  getCostSummary,
  listCostEventsPage,
} from '@optum/api-client/endpoints/cost';
import { listOrganizations } from '@optum/api-client/endpoints/organizations';
import type { CostEvent, OrganizationCost, Payer } from '@optum/api-client/types';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { absoluteDateTime, quantity, thb } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';

import { DataTable } from '../../ui/DataTable.js';
import { ListShape } from '../../ui/shapes.js';
import { SectionHeader } from '../../ui/SectionHeader.js';
import { StatRow } from '../../ui/StatRow.js';
import { Tabs, type TabItem } from '../../ui/Tabs.js';
import { RateCardTab } from './RateCardTab.js';
import { Badge, Pagination } from '../../ui/finixt.js';
import { Select } from '../../ui/Select.js';
import { ExportMenu } from './ExportMenu.js';
import { PeriodPicker } from './PeriodPicker.js';
import { GapBanner, GapChips } from './GapBanner.js';
import { monthInstants, monthOf, monthRange } from './period.js';

const PAGE_SIZE = 50;
const ANY = '';
/** Width of each Events filter, so the four sit in one row. */
const FILTER_WIDTH = 200;

const STATUS: Record<
  CostEvent['status'],
  { label: string; color: 'ok' | 'neutral' | 'warn' | 'info' }
> = {
  priced: { label: 'rated', color: 'ok' },
  reported: { label: 'reported', color: 'info' },
  included: { label: 'included', color: 'neutral' },
  unpriced: { label: 'unpriced', color: 'neutral' },
  no_rate: { label: 'no rate', color: 'warn' },
  no_fx: { label: 'no exchange rate', color: 'warn' },
};

/** "in 6,306 · out 498": the quantity map as one compact line. */
export function quantityText(q: Record<string, number>): string {
  const short = (unit: string) =>
    unit
      .replace(/^input_tokens$/, 'in')
      .replace(/^output_tokens$/, 'out')
      .replace(/_tokens$/, '');
  return Object.entries(q)
    .map(([unit, value]) => `${short(unit)} ${quantity(value)}`)
    .join(' · ');
}

type CostTab = 'overview' | 'rate-card';

const COST_TABS: TabItem<CostTab>[] = [
  { value: 'overview', label: 'Overview' },
  // Set up once and revised when a vendor's price moves. It prices what this
  // page shows, so it lives here (Feature 70); it was a Security tab before.
  { value: 'rate-card', label: 'Rate card' },
];

export function PlatformCostPage() {
  const [params, setParams] = useSearchParams();
  const tab: CostTab = params.get('tab') === 'rate-card' ? 'rate-card' : 'overview';
  function selectTab(next: CostTab) {
    setParams(next === 'overview' ? {} : { tab: next }, { replace: true });
  }
  const [month, setMonth] = useState(() => monthOf(new Date()));
  const [organizationId, setOrganizationId] = useState(ANY);
  const [capabilityId, setCapabilityId] = useState(ANY);
  const [usageType, setUsageType] = useState(ANY);
  const [payer, setPayer] = useState<Payer | ''>(ANY);
  const [page, setPage] = useState(0);

  const days = monthRange(month);
  const filters = useMemo(
    () => ({
      ...monthInstants(month),
      ...(organizationId ? { organization_id: organizationId } : {}),
      ...(capabilityId ? { capability_id: capabilityId } : {}),
      ...(usageType ? { usage_type: usageType } : {}),
      ...(payer ? { payer } : {}),
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [month, organizationId, capabilityId, usageType, payer, page],
  );

  const summary = useQuery({
    queryKey: qk.costSummary(days.start, days.end),
    queryFn: () => getCostSummary(days),
  });
  const events = useQuery({
    queryKey: qk.costEvents(filters),
    queryFn: () => listCostEventsPage(filters),
  });
  const organizations = useQuery({
    queryKey: qk.orgs(),
    queryFn: () => listOrganizations(),
  });
  const capabilities = useQuery({ queryKey: qk.capabilities(), queryFn: listCapabilities });
  const choices = useQuery({ queryKey: qk.costChoices(), queryFn: getCostChoices });

  const orgName = useMemo(() => {
    const names = new Map<string, string>();
    for (const o of organizations.data ?? []) names.set(o.id, o.name);
    for (const o of summary.data ?? []) names.set(o.organization_id, o.name);
    return (id: string) => names.get(id) ?? id;
  }, [organizations.data, summary.data]);
  // Every type the platform knows (Feature 63), not only those on this page,
  // so a filter can be set before the page shows the type.
  const usageTypes = useMemo(() => {
    const seen = new Set<string>();
    for (const t of choices.data?.types ?? []) seen.add(t.usage_type);
    for (const e of events.data?.items ?? []) seen.add(e.usage_type);
    if (usageType) seen.add(usageType);
    return [...seen].sort();
  }, [choices.data, events.data, usageType]);
  const gaps = useMemo(
    () => ({
      no_rate_types: [...new Set((summary.data ?? []).flatMap((o) => o.no_rate_types))].sort(),
      no_fx_types: [...new Set((summary.data ?? []).flatMap((o) => o.no_fx_types))].sort(),
    }),
    [summary.data],
  );

  function pick<T extends string>(set: (v: T) => void) {
    return (v: T) => {
      set(v);
      setPage(0);
    };
  }

  return (
    <ListShape
      title="Cost"
      subtitle="Every organization's usage: the vendors' receipts where they exist, the platform's rate card where they do not. Open an organization for its own month, or a task for its lines."
      action={
        tab === 'rate-card' ? undefined : (
          <ExportMenu
            storageKey="platform"
            run={(columns) =>
              exportCostEvents(
                {
                  start: days.start,
                  end: days.end,
                  ...(organizationId ? { organization_id: organizationId } : {}),
                  ...(capabilityId ? { capability_id: capabilityId } : {}),
                  ...(usageType ? { usage_type: usageType } : {}),
                  ...(payer ? { payer } : {}),
                },
                columns,
              )
            }
          />
        )
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22, paddingBottom: 64 }}>
        <Tabs items={COST_TABS} value={tab} onChange={selectTab} />

        {tab === 'rate-card' ? (
          <RateCardTab />
        ) : (
          <>
            <PeriodPicker value={month} onChange={(m) => pick(setMonth)(m)} />
            <GapBanner gaps={gaps} />

            <section style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
              <SectionHeader
                title="By organization"
                subtitle="One row per organization with usage in the period."
              />
              {summary.isPending && <LoadingBlock />}
              {summary.isError && (
                <ErrorBlock
                  message={
                    summary.error instanceof Error ? summary.error.message : 'Failed to load'
                  }
                />
              )}
              {summary.data && <SummaryTable rows={summary.data} />}
            </section>

            <section style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
              <SectionHeader
                title="Events"
                subtitle="Newest first. The figure is the event's receipt converted, or the sum of its rated lines; the status is the worst of them."
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 7 }}>
                <Select
                  searchable
                  aria-label="Organization"
                  width={FILTER_WIDTH}
                  value={organizationId}
                  onChange={(value) => pick(setOrganizationId)(value)}
                  options={[
                    { value: ANY, label: 'Any organization' },
                    ...(organizations.data ?? []).map((o) => ({ value: o.id, label: o.name })),
                  ]}
                />
                <Select
                  searchable
                  aria-label="Capability"
                  width={FILTER_WIDTH}
                  value={capabilityId}
                  onChange={(value) => pick(setCapabilityId)(value)}
                  options={[
                    { value: ANY, label: 'Any capability' },
                    ...(capabilities.data ?? []).map((c) => ({ value: c.id, label: c.id })),
                  ]}
                />
                <Select
                  searchable
                  aria-label="Usage type"
                  width={FILTER_WIDTH}
                  value={usageType}
                  onChange={(value) => pick(setUsageType)(value)}
                  options={[
                    { value: ANY, label: 'Any type' },
                    ...usageTypes.map((t) => ({ value: t, label: t })),
                  ]}
                />
                <Select
                  aria-label="Payer"
                  width={FILTER_WIDTH}
                  value={payer}
                  onChange={(value) => pick<Payer | ''>(setPayer)(value as Payer | '')}
                  options={[
                    { value: ANY, label: 'Any payer' },
                    { value: 'organization', label: 'organization' },
                    { value: 'platform', label: 'platform' },
                  ]}
                />
              </div>
              {events.isPending && <LoadingBlock />}
              {events.isError && (
                <ErrorBlock
                  message={events.error instanceof Error ? events.error.message : 'Failed to load'}
                />
              )}
              {events.data && events.data.items.length === 0 && (
                <EmptyBlock>No usage events match these filters.</EmptyBlock>
              )}
              {events.data && events.data.items.length > 0 && (
                <>
                  <StatRow
                    tiles={[
                      {
                        label: 'Events in period',
                        value: quantity(events.data.total),
                        icon: 'receipt_long',
                      },
                      {
                        label: 'Costed on this page',
                        value: thb(
                          events.data.items.reduce((sum, e) => sum + Number(e.priced_amount), 0),
                        ),
                        icon: 'payments',
                      },
                    ]}
                  />
                  <LedgerTable rows={events.data.items} orgName={orgName} />
                  <Pagination
                    total={events.data.total}
                    page={page + 1}
                    pageSize={PAGE_SIZE}
                    onChange={(next) => setPage(next - 1)}
                  />
                </>
              )}
            </section>
          </>
        )}
      </div>
    </ListShape>
  );
}

function SummaryTable({ rows }: { rows: OrganizationCost[] }) {
  if (rows.length === 0)
    return <EmptyBlock>No organization recorded usage in this period.</EmptyBlock>;
  return (
    <DataTable
      rows={rows}
      rowKey="organization_id"
      columns={[
        { key: 'name', label: 'Organization', strong: true },
        {
          key: 'events',
          label: 'Events',
          align: 'right',
          mono: true,
          render: (o) => quantity(o.events),
        },
        {
          key: 'priced_total',
          label: 'Cost',
          align: 'right',
          mono: true,
          render: (o) => thb(o.priced_total),
        },
        {
          key: 'reported_share',
          label: 'Reported',
          align: 'right',
          mono: true,
          render: (o) => thb(o.reported_share),
        },
        {
          key: 'rated_share',
          label: 'Rated',
          align: 'right',
          mono: true,
          render: (o) => thb(o.rated_share),
        },
        { key: 'gaps', label: 'Gaps', render: (o) => <GapChips gaps={o} Chip={AmberChip} /> },
        {
          key: 'open',
          label: '',
          render: (o) => (
            <Link
              to={`/orgs/${o.organization_id}/cost`}
              style={{ fontWeight: 'var(--weight-semibold)', color: 'var(--text-accent)' }}
            >
              Open
            </Link>
          ),
        },
      ]}
    />
  );
}

function LedgerTable({ rows, orgName }: { rows: CostEvent[]; orgName: (id: string) => string }) {
  return (
    <DataTable
      rows={rows}
      columns={[
        { key: 'at', label: 'When', mono: true, render: (e) => absoluteDateTime(e.at) },
        {
          key: 'organization_id',
          label: 'Organization',
          render: (e) => orgName(e.organization_id),
        },
        {
          key: 'capability',
          label: 'Capability · task',
          render: (e) => (
            <>
              <span style={{ fontSize: 'var(--text-meta-lg)' }} className="mono">
                {e.capability_id ?? 'ingestion'}
              </span>
              <br />
              <Link
                to={`/tasks/${e.subject_id}`}
                style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-accent)' }}
                className="mono"
              >
                {e.subject_id.slice(0, 8)}
              </Link>
              {e.subject_kind === 'ingestion_job' && <Badge tone="neutral">ingestion</Badge>}
            </>
          ),
        },
        {
          key: 'type',
          label: 'Type · resource',
          wrap: true,
          render: (e) => (
            <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 5 }}>
              <Badge tone="info">{e.usage_type}</Badge>
              <code
                style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}
                className="mono"
              >
                {e.resource}
              </code>
              {!e.declared && <Badge tone="danger">undeclared</Badge>}
              {e.estimated && <Badge tone="warn">estimated</Badge>}
            </span>
          ),
        },
        { key: 'quantity', label: 'Quantity', mono: true, render: (e) => quantityText(e.quantity) },
        { key: 'payer', label: 'Payer', render: (e) => <Badge tone="neutral">{e.payer}</Badge> },
        {
          key: 'priced_amount',
          label: 'Cost',
          align: 'right',
          mono: true,
          render: (e) => (e.line_count > 0 ? thb(e.priced_amount) : '—'),
        },
        {
          key: 'status',
          label: 'Status',
          render: (e) => (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Badge tone={STATUS[e.status].color}>{STATUS[e.status].label}</Badge>
              {e.reason && (
                <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
                  {e.reason}
                </span>
              )}
            </span>
          ),
        },
      ]}
    />
  );
}

function AmberChip({ label }: { label: string }) {
  return <Badge tone="warn">{label}</Badge>;
}
